import React, { useState, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Upload, Settings as SettingsIcon, Users, Bell, Database, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Settings = () => {
  const { token } = useAuth();
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      return;
    }

    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/import/excel`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(response.data.message);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const downloadSampleTemplate = async () => {
    try {
      const response = await axios.get(`${API}/download/sample-import-template`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Clarity_Strategy_Import_Template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      
      toast.success('Sample template downloaded!');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  return (
    <div className="space-y-8" data-testid="settings-page">
      <div className="page-header">
        <h1 className="page-title flex items-center gap-3">
          <SettingsIcon className="text-cyan-500" size={32} />
          Settings
        </h1>
        <p className="page-subtitle">Configure app settings, imports, and notifications</p>
      </div>

      {/* Import Section */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-lg">
            <Upload className="text-cyan-600" size={24} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">Import Data</h2>
            <p className="text-sm text-slate-600">Upload Excel file to import tasks and users</p>
          </div>
        </div>

        <div className="bg-slate-50 rounded-lg p-6 border-2 border-dashed border-slate-300">
          <div className="text-center">
            <Upload className="mx-auto text-slate-400 mb-3" size={48} />
            <h3 className="font-semibold text-slate-800 mb-2">Upload Excel File</h3>
            <p className="text-sm text-slate-600 mb-4">
              Supported format: .xlsx with sheet "Old data"
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileUpload}
              className="hidden"
              id="file-upload"
              data-testid="file-upload-input"
            />
            <div className="flex gap-3 justify-center">
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
                data-testid="upload-button"
                className="btn-cta"
              >
                {importing ? 'Importing...' : 'Choose File'}
              </Button>
              <Button
                onClick={downloadSampleTemplate}
                variant="outline"
                data-testid="download-template-button"
                className="border-cyan-300 text-cyan-700 hover:bg-cyan-50"
              >
                <Download size={18} className="mr-2" />
                Download Sample
              </Button>
            </div>
          </div>
        </div>

        <div className="mt-4 space-y-3">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> The import will create users from the "Task Done By" column and import all tasks with their details.
              Existing users with matching names will be reused.
            </p>
          </div>
          <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
            <p className="text-sm text-cyan-800">
              <strong>💡 Tip:</strong> Download the sample template above to see the exact format required. 
              The Excel file must have a sheet named "Old data" with specific column names.
            </p>
          </div>
        </div>
      </div>

      {/* Notification Settings */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-lg">
            <Bell className="text-cyan-600" size={24} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">Notification Settings</h2>
            <p className="text-sm text-slate-600">Manage automated emails and alerts</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between py-3 border-b border-slate-100">
            <div>
              <p className="font-medium text-slate-800">Daily Digest Email</p>
              <p className="text-sm text-slate-600">Sent to managers at 9:00 AM IST</p>
            </div>
            <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
              Active
            </span>
          </div>
          <div className="flex items-center justify-between py-3 border-b border-slate-100">
            <div>
              <p className="font-medium text-slate-800">Developer Completion Alerts</p>
              <p className="text-sm text-slate-600">Notify task owners when dev work completes</p>
            </div>
            <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
              Active
            </span>
          </div>
          <div className="flex items-center justify-between py-3">
            <div>
              <p className="font-medium text-slate-800">Overdue Task Reminders</p>
              <p className="text-sm text-slate-600">Alert managers about overdue tasks</p>
            </div>
            <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
              Active
            </span>
          </div>
        </div>
      </div>

      {/* System Info */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-lg">
            <Database className="text-cyan-600" size={24} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">System Information</h2>
            <p className="text-sm text-slate-600">App configuration and status</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-slate-50 rounded-lg p-4">
            <p className="text-sm text-slate-600 mb-1">Database</p>
            <p className="font-semibold text-slate-900">MongoDB (clarity_seo_db)</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-4">
            <p className="text-sm text-slate-600 mb-1">Email Service</p>
            <p className="font-semibold text-slate-900">Hostinger SMTP</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-4">
            <p className="text-sm text-slate-600 mb-1">AI Provider</p>
            <p className="font-semibold text-slate-900">OpenAI GPT-5.1</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-4">
            <p className="text-sm text-slate-600 mb-1">Timezone</p>
            <p className="font-semibold text-slate-900">IST (Asia/Kolkata)</p>
          </div>
        </div>
      </div>

      {/* Admin Guide */}
      <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl border border-cyan-200 p-6">
        <h2 className="text-xl font-bold text-slate-900 mb-4">Admin Guide</h2>
        <div className="space-y-3 text-sm text-slate-700">
          <div>
            <strong className="text-cyan-700">1. Import Data:</strong> Upload the Excel file using the import section above.
            The system will automatically create users and tasks.
          </div>
          <div>
            <strong className="text-cyan-700">2. Add User Types:</strong> Navigate to "User Types" to create and manage
            team roles with specific permissions.
          </div>
          <div>
            <strong className="text-cyan-700">3. Manage Users:</strong> Users are auto-created during import or can register
            themselves. Update their role levels from the Task Table.
          </div>
          <div>
            <strong className="text-cyan-700">4. Daily Digest:</strong> Managers receive automated emails at 9 AM IST with
            team performance summaries.
          </div>
          <div>
            <strong className="text-cyan-700">5. Archive Rules:</strong> Tasks older than the configured retention period
            are automatically archived weekly.
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;